import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLogin from './AdminLogin';
import RepairsDashboard from './RepairsDashboard';
import config from '../config';
import '../styles/repair-app.css';

const DEFAULT_TITLE = 'A2zTak Admin - Easy Finance Management System';
const DEFAULT_MANIFEST = `${process.env.PUBLIC_URL || ''}/manifest.json`;
const REPAIR_MANIFEST = `${process.env.PUBLIC_URL || ''}/repair-manifest.json`;

function RepairCounterApp() {
  const { user, login, logout, loading } = useAuth();
  const [readyCount, setReadyCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const loadReady = useCallback(async () => {
    try {
      const res = await axios.get(`${config.API_URL}/repairs/stats`);
      setReadyCount(res.data?.readyForPickup || 0);
    } catch {
      /* ignore */
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshTick((t) => t + 1);
    await loadReady();
    setTimeout(() => setRefreshing(false), 600);
  }, [loadReady]);

  useEffect(() => {
    document.title = 'A2ZTAK Repair';
    const manifest = document.querySelector('link[rel="manifest"]');
    const prevManifest = manifest?.getAttribute('href') || DEFAULT_MANIFEST;
    if (manifest) manifest.setAttribute('href', REPAIR_MANIFEST);

    let appleCap = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    const createdCap = !appleCap;
    if (!appleCap) {
      appleCap = document.createElement('meta');
      appleCap.name = 'apple-mobile-web-app-capable';
      document.head.appendChild(appleCap);
    }
    appleCap.content = 'yes';

    let theme = document.querySelector('meta[name="theme-color"]');
    const prevTheme = theme?.getAttribute('content') || '#000000';
    if (theme) theme.setAttribute('content', '#1e40af');

    let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
    const prevIcon = appleIcon?.getAttribute('href');
    if (appleIcon) appleIcon.setAttribute('href', `${process.env.PUBLIC_URL || ''}/repair-icon.svg`);

    return () => {
      document.title = DEFAULT_TITLE;
      if (manifest) manifest.setAttribute('href', prevManifest);
      if (theme) theme.setAttribute('content', prevTheme);
      if (appleIcon && prevIcon) appleIcon.setAttribute('href', prevIcon);
      if (createdCap && appleCap?.parentNode) appleCap.parentNode.removeChild(appleCap);
    };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    loadReady();
    const t = setInterval(loadReady, 45000);
    return () => clearInterval(t);
  }, [user, loadReady]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center repair-app-shell">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="repair-app-shell">
        <header className="repair-app-topbar">
          <div className="repair-app-topbar-brand">
            <div className="repair-app-logo"><i className="bi bi-tools" /></div>
            <div>
              <div className="repair-app-topbar-title">A2ZTAK Repair</div>
              <div className="repair-app-topbar-sub">Counter login</div>
            </div>
          </div>
        </header>
        <div className="repair-login-wrap">
          <div className="repair-login-card">
            <AdminLogin onLogin={login} />
          </div>
        </div>
      </div>
    );
  }

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div className="repair-app-shell">
      <header className="repair-app-topbar">
        <div className="repair-app-topbar-brand">
          <div className="repair-app-logo"><i className="bi bi-tools" /></div>
          <div>
            <div className="repair-app-topbar-title">A2ZTAK Repair</div>
            <div className="repair-app-topbar-sub">{today} · Punjab</div>
          </div>
        </div>
        <div className="repair-app-topbar-actions d-flex align-items-center gap-2">
          {readyCount > 0 && (
            <span className="repair-app-ready-pill">{readyCount} ready</span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-light"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh"
          >
            <i className={`bi bi-arrow-clockwise ${refreshing ? 'spin-refresh' : ''}`} />
          </button>
          <button type="button" className="btn btn-sm btn-light text-primary fw-semibold" onClick={logout}>
            <i className="bi bi-box-arrow-right" />
          </button>
        </div>
      </header>
      <main className="repair-app-main">
        <RepairsDashboard mobileApp onStatsChange={loadReady} refreshTick={refreshTick} />
      </main>
    </div>
  );
}

export default RepairCounterApp;
